import { useContext, useState, useEffect, useCallback } from 'react';
import { GlobalContext } from '../../context/GlobalContext';
import { callAPI } from '../../hooks/api';
import { Trash2, AlertTriangle, CheckCircle, XCircle, Loader2 } from 'lucide-react';
import { Dialog } from '../../components/Dialog';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import Textarea from '../../components/Textarea';
import { Button } from '../../shadcn/Button';
import firestore from '../../firestore';

export default function LoansBatchDelete() {
	const { internalCountry, userData } = useContext(GlobalContext);
	const country = internalCountry?.get() || 'venezuela';

	const [deviceIdsInput, setDeviceIdsInput] = useState('');
	const [loading, setLoading] = useState(false);
	const [alertDialog, setAlertDialog] = useState<{
		title: string;
		message: string;
		type?: 'success' | 'error' | 'warning';
	} | null>(null);
	const [confirmDialog, setConfirmDialog] = useState(false);
	const [transforming, setTransforming] = useState(false);
	const [transformationResults, setTransformationResults] = useState<{
		deviceConfirmationIds: string[];
		transformed: Map<string, string>; // device_confirmation_id -> document_id
		notFound: string[];
		invalidDocumentIds: string[]; // Document IDs that don't have 20 characters
	}>({ deviceConfirmationIds: [], transformed: new Map(), notFound: [], invalidDocumentIds: [] });
	const [transformedDeviceIds, setTransformedDeviceIds] = useState<string[]>([]);

	const parseDeviceIds = (input: string): string[] => {
		// Split by newlines, commas, or semicolons and filter out empty strings
		return Array.from(
			new Set(
				input
					.split(/[\n,;]+/)
					.map((id) => id.trim())
					.filter((id) => id.length > 0)
			)
		);
	};

	const isDeviceConfirmationId = (id: string): boolean => {
		// Device confirmation IDs are exactly 10 characters long
		return id.length === 10;
	};

	const isValidDocumentId = (id: string): boolean => {
		// Document IDs must be exactly 20 characters long
		return id.length === 20;
	};

	const transformDeviceConfirmationIds = useCallback(async (deviceConfirmationIds: string[]): Promise<Map<string, string>> => {
		const transformed = new Map<string, string>();

		if (deviceConfirmationIds.length === 0) {
			return transformed;
		}

		try {
			// Query Devices collection for each device_confirmation_id
			// We'll query in batches to avoid too many queries
			const batchSize = 10;
			for (let i = 0; i < deviceConfirmationIds.length; i += batchSize) {
				const batch = deviceConfirmationIds.slice(i, i + batchSize);

				// Query each device_confirmation_id with country filter
				const promises = batch.map(async (confirmationId) => {
					try {
						// Try as string first
						let querySnapshot = await firestore
							.collection('Devices')
							.where('device_confirmation_id', '==', confirmationId)
							.limit(1)
							.get();

						if (!querySnapshot.empty) {
							const doc = querySnapshot.docs[0];
							return { confirmationId, documentId: doc.id };
						}
						return { confirmationId, documentId: null };
					} catch (error) {
						console.error(`Error querying device_confirmation_id ${confirmationId}:`, error);
						return { confirmationId, documentId: null };
					}
				});

				const results = await Promise.all(promises);
				results.forEach(({ confirmationId, documentId }) => {
					if (documentId) {
						transformed.set(confirmationId, documentId);
					}
				});
			}
		} catch (error) {
			console.error('Error transforming device confirmation IDs:', error);
		}

		return transformed;
	}, []);

	// Transform device confirmation IDs when input changes
	useEffect(() => {
		const transformIds = async () => {
			const ids = parseDeviceIds(deviceIdsInput);

			if (ids.length === 0) {
				setTransformedDeviceIds([]);
				setTransformationResults({ deviceConfirmationIds: [], transformed: new Map(), notFound: [], invalidDocumentIds: [] });
				return;
			}

			// Separate device confirmation IDs from document IDs
			const deviceConfirmationIds = ids.filter(isDeviceConfirmationId);
			const documentIds = ids.filter((id) => !isDeviceConfirmationId(id));

			// Validate document IDs
			const validDocumentIds = documentIds.filter(isValidDocumentId);
			const invalidDocumentIds = documentIds.filter((id) => !isValidDocumentId(id));

			if (deviceConfirmationIds.length === 0 && invalidDocumentIds.length === 0) {
				// No transformation needed and all document IDs are valid
				setTransformedDeviceIds(validDocumentIds);
				setTransformationResults({ deviceConfirmationIds: [], transformed: new Map(), notFound: [], invalidDocumentIds: [] });
				return;
			}

			// Start transformation
			setTransforming(true);
			setTransformationResults({
				deviceConfirmationIds,
				transformed: new Map(),
				notFound: [],
				invalidDocumentIds
			});

			try {
				const transformedMap = await transformDeviceConfirmationIds(deviceConfirmationIds);

				// Find which IDs were not found
				const notFound = deviceConfirmationIds.filter((id) => !transformedMap.has(id));

				// Combine valid document IDs with transformed IDs
				const allDocumentIds = [...validDocumentIds, ...Array.from(transformedMap.values())];

				setTransformedDeviceIds(allDocumentIds);
				setTransformationResults({
					deviceConfirmationIds,
					transformed: transformedMap,
					notFound,
					invalidDocumentIds
				});
			} catch (error) {
				console.error('Error in transformation:', error);
				setTransformationResults({
					deviceConfirmationIds,
					transformed: new Map(),
					notFound: deviceConfirmationIds,
					invalidDocumentIds
				});
				// Still allow using valid document IDs if transformation fails
				setTransformedDeviceIds(validDocumentIds);
			} finally {
				setTransforming(false);
			}
		};

		const timeoutId = setTimeout(() => {
			transformIds();
		}, 500); // Debounce for 500ms

		return () => clearTimeout(timeoutId);
	}, [deviceIdsInput, country, transformDeviceConfirmationIds]);

	const handleDeleteLoans = async (): Promise<true | string> => {
		// Use transformed device IDs (document IDs only)
		const deviceIds = transformedDeviceIds;

		if (deviceIds.length === 0) {
			setAlertDialog({
				title: 'Error',
				message: 'Por favor ingrese al menos un ID de dispositivo válido',
				type: 'warning'
			});
			return 'No device IDs provided';
		}

		// Check if there are device IDs that couldn't be validated
		if (transformationResults.notFound.length > 0 || transformationResults.invalidDocumentIds.length > 0) {
			const warnings = [];
			if (transformationResults.notFound.length > 0) {
				warnings.push(
					`${transformationResults.notFound.length} ID${transformationResults.notFound.length !== 1 ? 's' : ''} de dispositivo no encontrado${transformationResults.notFound.length !== 1 ? 's' : ''}`
				);
			}
			if (transformationResults.invalidDocumentIds.length > 0) {
				warnings.push(
					`${transformationResults.invalidDocumentIds.length} ID${transformationResults.invalidDocumentIds.length !== 1 ? 's' : ''} de dispositivo inválido${transformationResults.invalidDocumentIds.length !== 1 ? 's' : ''} (deben tener 10 o 20 caracteres)`
				);
			}
			setAlertDialog({
				title: 'Advertencia',
				message: `${warnings.join('. ')}. Solo se eliminarán los IDs válidos.`,
				type: 'warning'
			});
		}

		setLoading(true);

		try {
			const [result, error] = await callAPI(
				'https://api.krece.app/administration/async_delete_loans/',
				'POST',
				{ device_ids: deviceIds },
				userData,
				country
			);

			if (error) {
				setAlertDialog({
					title: 'Error',
					message: `Error al eliminar los préstamos: ${error}`,
					type: 'error'
				});
				return error;
			}

			if (result?.success) {
				setAlertDialog({
					title: 'Éxito',
					message: `Se han eliminado ${deviceIds.length} préstamo${deviceIds.length !== 1 ? 's' : ''} exitosamente.`,
					type: 'success'
				});
				setDeviceIdsInput(''); // Clear the input on success
			} else {
				setAlertDialog({
					title: 'Advertencia',
					message: result?.status || 'La operación se completó con advertencias',
					type: 'warning'
				});
			}
		} catch (error) {
			setAlertDialog({
				title: 'Error',
				message: 'Ha ocurrido un error inesperado al eliminar los préstamos',
				type: 'error'
			});
			console.error('Error deleting loans:', error);
		} finally {
			setLoading(false);
		}

		return true;
	};

	const handleConfirmDelete = async (): Promise<true | string> => {
		setConfirmDialog(false);
		return await handleDeleteLoans();
	};

	return (
		<div className="mx-auto w-full max-w-4xl space-y-8 p-6">
			{alertDialog && (
				<Dialog title={alertDialog.title} onClose={() => setAlertDialog(null)} closable={true}>
					<div className="flex items-start gap-3">
						{alertDialog.type === 'success' && <CheckCircle className="mt-0.5 h-5 w-5 flex-shrink-0 text-green-500" />}
						{alertDialog.type === 'error' && <XCircle className="mt-0.5 h-5 w-5 flex-shrink-0 text-red-500" />}
						{alertDialog.type === 'warning' && <AlertTriangle className="mt-0.5 h-5 w-5 flex-shrink-0 text-yellow-500" />}
						<p className="text-neutral-200">{alertDialog.message}</p>
					</div>
				</Dialog>
			)}

			{confirmDialog && (
				<ConfirmDialog
					title="Confirmar Eliminación de Préstamos"
					message={`¿Estás seguro de que deseas eliminar ${transformedDeviceIds.length} préstamo${transformedDeviceIds.length !== 1 ? 's' : ''}? Esta acción no se puede deshacer.`}
					onConfirm={handleConfirmDelete}
					onClose={() => setConfirmDialog(false)}
				/>
			)}

			{/* Header */}
			<div className="space-y-4">
				<div className="flex items-center gap-4">
					<div className="rounded-lg bg-red-500/10 p-3">
						<Trash2 className="h-8 w-8 text-red-500" />
					</div>
					<div>
						<h1 className="text-3xl font-bold text-neutral-100">Eliminación de Préstamos</h1>
						<p className="text-neutral-400">Elimina múltiples préstamos en batch</p>
					</div>
				</div>
			</div>

			{/* Main Content */}
			<div className="rounded-xl border border-neutral-800 bg-neutral-900/50 p-8">
				<div className="space-y-6">
					<div className="space-y-4">
						<p className="text-sm text-neutral-300">
							Ingrese los IDs de dispositivo que desea eliminar. Puede pegar múltiples IDs separados por líneas, comas o puntos y
							comas.
							<br />
							<span className="text-xs text-neutral-400 mt-1 block">
								Los IDs de dispositivo pueden tener 10 o 20 caracteres.
							</span>
						</p>

						<div className="rounded-xl border border-dashed border-neutral-700 bg-neutral-900/30 p-6">
							<div className="space-y-4">
								<div className="space-y-2">
									<Textarea
										placeholder="IDs de dispositivo (uno por línea o separados por comas)"
										value={deviceIdsInput}
										onChange={setDeviceIdsInput}
										rows={8}
										containerClassName="w-full"
										disabled={loading || transforming}
									/>
									<div className="flex items-center justify-between">
										<p className="text-xs text-neutral-400">Pegue múltiples IDs separados por líneas o comas.</p>
										{transforming ? (
											<span className="flex items-center gap-2 text-xs text-neutral-400">
												<Loader2 className="h-3 w-3 animate-spin" />
												Validando IDs...
											</span>
										) : (
											transformedDeviceIds.length > 0 && (
												<span className="text-xs text-neutral-400">
													{transformedDeviceIds.length} préstamo{transformedDeviceIds.length !== 1 ? 's' : ''} listo
													{transformedDeviceIds.length !== 1 ? 's' : ''}
												</span>
											)
										)}
									</div>
								</div>

								<Button
									variant="primary"
									onClick={() => setConfirmDialog(true)}
									className="w-full bg-red-600 text-white hover:bg-red-700"
									disabled={loading || transforming || transformedDeviceIds.length === 0}
									loading={loading}
								>
									<Trash2 className="mr-2 h-4 w-4" />
									Eliminar{' '}
									{transformedDeviceIds.length > 0
										? `${transformedDeviceIds.length} Préstamo${transformedDeviceIds.length !== 1 ? 's' : ''}`
										: 'Préstamos'}
								</Button>
							</div>
						</div>
					</div>

					{/* Preview Section */}
					{(transformedDeviceIds.length > 0 ||
						transformationResults.notFound.length > 0 ||
						transformationResults.invalidDocumentIds.length > 0) &&
						!transforming && (
							<div className="space-y-4">
								<div className="flex items-center gap-3 border-b border-neutral-800 pb-4">
									<div className="rounded-lg bg-blue-500/10 p-2">
										<CheckCircle className="h-5 w-5 text-blue-500" />
									</div>
									<h3 className="text-lg font-semibold text-neutral-100">IDs de Dispositivo</h3>
								</div>

								{/* Validation Messages */}
								{(transformationResults.notFound.length > 0 || transformationResults.invalidDocumentIds.length > 0) && (
									<div className="space-y-2 rounded-lg border border-neutral-800 bg-neutral-900/50 p-4">
										{transformationResults.notFound.length > 0 && (
											<div className="text-xs text-yellow-400">
												⚠ {transformationResults.notFound.length} ID{transformationResults.notFound.length !== 1 ? 's' : ''} de dispositivo
												{transformationResults.notFound.length !== 1 ? 's' : ''} no encontrado{transformationResults.notFound.length !== 1 ? 's' : ''}:{' '}
												{transformationResults.notFound.slice(0, 5).join(', ')}
												{transformationResults.notFound.length > 5 ? '...' : ''}
											</div>
										)}
										{transformationResults.invalidDocumentIds.length > 0 && (
											<div className="text-xs text-red-400">
												✗ {transformationResults.invalidDocumentIds.length} ID
												{transformationResults.invalidDocumentIds.length !== 1 ? 's' : ''} de dispositivo inválido
												{transformationResults.invalidDocumentIds.length !== 1 ? 's' : ''} (deben tener 10 o 20 caracteres):{' '}
												{transformationResults.invalidDocumentIds.slice(0, 5).join(', ')}
												{transformationResults.invalidDocumentIds.length > 5 ? '...' : ''}
											</div>
										)}
									</div>
								)}

								{/* Valid Device IDs Preview */}
								{transformedDeviceIds.length > 0 && (
									<div className="max-h-60 overflow-y-auto rounded-xl border border-neutral-800 bg-neutral-900/30 p-4">
										<div className="mb-2 text-xs font-medium text-neutral-300">
											{transformedDeviceIds.length} ID{transformedDeviceIds.length !== 1 ? 's' : ''} de dispositivo válido
											{transformedDeviceIds.length !== 1 ? 's' : ''}:
										</div>
										<div className="grid gap-2 md:grid-cols-2 lg:grid-cols-3">
											{transformedDeviceIds.map((deviceId, idx) => {
												return (
													<div key={idx} className="flex items-center gap-2 rounded-lg bg-neutral-800/50 p-3">
														<div className="h-2 w-2 rounded-full bg-green-500"></div>
														<div className="min-w-0 flex-1">
															<code className="block truncate font-mono text-sm text-neutral-200" title={deviceId}>
																{deviceId}
															</code>
														</div>
													</div>
												);
											})}
										</div>
									</div>
								)}
							</div>
						)}
				</div>
			</div>
		</div>
	);
}
